import React, { useState, useEffect } from "react";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";
import RegulationModal from "./RegulationModal"; 
import "./RegulationList.css"; // Dùng chung file CSS style hệ thống

const PAGE_SIZE = 10;

const RegulationList = () => {
  const [regulations, setRegulations] = useState([]);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("General"); // Tab mặc định thay đổi thành: General hoặc Personal
  const [showModal, setShowModal] = useState(false);
  const [editingRegulation, setEditingRegulation] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  
  // State quản lý việc đóng/mở rộng cây thư mục theo Công ty (Mặc định mở rộng hết)
  const [expandedCompanies, setExpandedCompanies] = useState([]);
  // State quản lý việc đóng/mở rộng các ID của dòng Cha (Main Document)
  const [expandedParentIds, setExpandedParentIds] = useState([]);

  useEffect(() => {
    fetchRegulations();
  }, []);

  // Thiết lập mặc định tự động mở rộng các nhóm công ty sau khi load dữ liệu thành công
  useEffect(() => {
    if (regulations.length > 0) {
      const distinctCompanies = [
        ...new Set(regulations.map((r) => r.companyName || r.company || "Target Company")),
      ];
      setExpandedCompanies(distinctCompanies);
    }
  }, [regulations]);

  // Hàm hỗ trợ xử lý khi gặp lỗi 401 Unauthorized
  const handleUnauthorizedError = (error) => {
    if (error.response && error.response.status === 401) {
      console.error("❌ Session expired or User ID not found. Redirecting to login...");
      alert("Your session has expired. Please log in again!");
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
      return true;
    }
    return false;
  };

  const fetchRegulations = async () => {
    try {
      const res = await axiosClient.get(API_ENDPOINTS.regulations);
      setRegulations(res.data || []);
    } catch (error) {
      console.error("❌ Lỗi load Regulations:", error);
      if (!handleUnauthorizedError(error)) {
        // Xử lý các lỗi khác ngoài lỗi 401 nếu cần
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Xóa văn bản này?")) return;
    try {
      await axiosClient.delete(`${API_ENDPOINTS.regulations}/${id}`);
      fetchRegulations();
    } catch (error) {
      console.error("Lỗi xóa:", error);
      if (!handleUnauthorizedError(error)) {
        alert("Xóa thất bại!");
      }
    }
  };

  const handleSendEmail = async (id) => {
    if (!window.confirm("Do you want to send notification email for this document?")) return;
    try {
      const url = API_ENDPOINTS.sendRegulationNotificationEmail(id);
      await axiosClient.post(url);
      alert("Notification email sent successfully!");
    } catch (error) {
      console.error("❌ Error sending email:", error);
      if (!handleUnauthorizedError(error)) {
        const serverMessage = error.response?.data?.message || "Failed to send email!";
        alert(serverMessage);
      }
    }
  };

const handleSave = async (formData) => {
  try {
    console.log("🚀 Sending to URL:", `${API_ENDPOINTS.regulations}/${formData.get("regulation.Id")}`);

    // 🔥 CẤU HÌNH ĐÚNG ĐỂ FIX 415:
    // Đừng truyền bất cứ header nào vào đây. 
    // Nếu axiosClient của ní có config mặc định là JSON, hãy truyền null cho headers:
    const config = {
      headers: {
        "Content-Type": undefined // Đây là mẹo: đặt undefined để trình duyệt tự thêm "multipart/form-data; boundary=..."
      }
    };

    if (editingRegulation) {
      await axiosClient.put(`${API_ENDPOINTS.regulations}/${formData.get("regulation.Id")}`, formData, config);
    } else {
      await axiosClient.post(API_ENDPOINTS.regulations, formData, config);
    }
    
    fetchRegulations();
    setShowModal(false);
  } catch (error) {
    console.error("❌ Save failed:", error);
    throw error;
  }
};

  // Hàm bổ trợ đồng bộ tạo URL đầy đủ từ file config của hệ thống
  const formatFileUrl = (url) => {
    if (!url) return "";
    const cleanUrl = url.replace(/\\/g, "/");
    let baseUrl = API_ENDPOINTS.url || axiosClient.defaults.baseURL || "";
    if (baseUrl.endsWith("/")) {
      baseUrl = baseUrl.slice(0, -1);
    }
    const cleanFileUrl = cleanUrl.startsWith("/") ? cleanUrl : "/" + cleanUrl;
    return cleanUrl.startsWith("http") ? cleanUrl : `${baseUrl}/api${cleanFileUrl}`;
  };

  // Đóng/mở rộng cây thư mục công ty ngoài cùng (Level 1)
  const toggleCompanyExpand = (companyName) => {
    setExpandedCompanies((prev) =>
      prev.includes(companyName)
        ? prev.filter((name) => name !== companyName)
        : [...prev, companyName]
    );
  };

  // Đóng/mở rộng văn bản gốc (Level 2)
  const toggleParentExpand = (parentId) => {
    setExpandedParentIds((prevIds) =>
      prevIds.includes(parentId)
        ? prevIds.filter((id) => id !== parentId)
        : [...prevIds, parentId]
    );
  };

  // --- LOGIC PHÂN NHÓM CÂY ĐỘNG 3 CẤP THEO TỪNG CÔNG TY THỰC TẾ ---
  const getHierarchicalFilteredData = () => {
    const matchesSearch = (r) => {
      if (!search) return true;
      const searchLower = search.toLowerCase();
      return (
        (r.title || "").toLowerCase().includes(searchLower) ||
        (r.content || "").toLowerCase().includes(searchLower) ||
        (r.notes || "").toLowerCase().includes(searchLower)
      );
    };

    // Lọc theo tab hiện tại (General hoặc Personal)
    const tabRegulations = regulations.filter((r) => r.type === activeTab);

    // Lấy danh sách tên tất cả các công ty xuất hiện trong dữ liệu phân loại
    const distinctCompanies = [
      ...new Set(tabRegulations.map((r) => r.companyName || r.company || "Target Company")),
    ];

    const resultTree = [];

    distinctCompanies.forEach((compName) => {
      // Lọc toàn bộ văn bản thuộc về công ty này
      const compRegulations = tabRegulations.filter(
        (r) => (r.companyName || r.company || "Target Company") === compName
      );

      const parents = compRegulations.filter((r) => !r.parentId);
      const children = compRegulations.filter((r) => r.parentId);

      const companySubNodes = [];

      parents.forEach((parent) => {
        const parentMatches = matchesSearch(parent);
        const itemChildren = children.filter((child) => String(child.parentId) === String(parent.id));
        const matchedChildren = itemChildren.filter(matchesSearch);

        if (parentMatches || matchedChildren.length > 0) {
          const isParentExpanded = expandedParentIds.includes(parent.id);

          const parentRow = {
            ...parent,
            isParentRow: true,
            hasChildren: itemChildren.length > 0,
            isExpanded: isParentExpanded,
            level: 2,
            companyGroup: compName
          };

          const childRows = [];
          if (isParentExpanded) {
            matchedChildren.forEach((child) => {
              childRows.push({ ...child, isParentRow: false, level: 3, companyGroup: compName });
            });
          }

          companySubNodes.push({ parentRow, childRows });
        }
      });

      // Xử lý các văn bản Con mồ côi (nếu có) theo từng công ty
      const orphanRows = [];
      children.forEach((child) => {
        const parentExists = parents.some((p) => String(p.id) === String(child.parentId));
        if (!parentExists && matchesSearch(child)) {
          orphanRows.push({ ...child, isParentRow: false, level: 2, companyGroup: compName });
        }
      });

      // Nếu nhóm công ty có dữ liệu hiển thị, đẩy node Công Ty ngoài cùng vào cây (Level 1)
      if (companySubNodes.length > 0 || orphanRows.length > 0) {
        const isCompExpanded = expandedCompanies.includes(compName);
        
        resultTree.push({
          id: `COMP_NODE_${compName}`,
          isCompanyRow: true,
          title: compName,
          content: "Corporate Unit Group",
          hasChildren: true,
          isExpanded: isCompExpanded,
          level: 1,
          companyGroup: compName
        });

        // Nếu cấp công ty đang được mở rộng, render tiếp các node con bên trong
        if (isCompExpanded) {
          companySubNodes.forEach((node) => {
            resultTree.push(node.parentRow);
            node.childRows.forEach((child) => resultTree.push(child));
          });
          orphanRows.forEach((orphan) => {
            resultTree.push(orphan);
          });
        }
      }
    });

    return resultTree;
  };

  const filteredTreeData = getHierarchicalFilteredData();

  // Thực hiện tính toán phân trang dựa trên cây đã dựng
  const totalPages = Math.ceil(filteredTreeData.length / PAGE_SIZE);
  const currentData = filteredTreeData.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const thStyle = { backgroundColor: "#f8f9fc", color: "#4e73df", fontWeight: "bold", fontSize: "0.8rem", textTransform: "uppercase" };

  return (
    <div className="container-fluid py-4">
      {/* Bootstrap Nav Tabs phân chia giữa General và Personal */}
      <ul className="nav nav-tabs border-bottom-0 mb-n1" style={{ position: "relative", zIndex: 1 }}>
        <li className="nav-item">
          <button 
            className={`nav-link px-4 fw-bold ${activeTab === "General" ? "active bg-white border-bottom-white text-primary" : "text-muted bg-light"}`}
            style={{ borderRadius: "8px 8px 0 0", border: "1px solid #dee2e6", borderBottom: activeTab === "General" ? "1px solid #fff" : "" }}
            onClick={() => {
              setActiveTab("General");
              setCurrentPage(1);
              setExpandedParentIds([]); // Reset trạng thái khi chuyển tab
            }}
          >
            <i className="fas fa-gavel me-2"></i>General
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link px-4 fw-bold ${activeTab === "Personal" ? "active bg-white border-bottom-white text-primary" : "text-muted bg-light"}`}
            style={{ borderRadius: "8px 8px 0 0", border: "1px solid #dee2e6", borderBottom: activeTab === "Personal" ? "1px solid #fff" : "" }}
            onClick={() => {
              setActiveTab("Personal");
              setCurrentPage(1);
              setExpandedParentIds([]); // Reset trạng thái khi chuyển tab
            }}
          >
            <i className="fas fa-book me-2"></i>Personal
          </button>
        </li>
      </ul>

      <div className="card shadow-sm border-0">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center flex-wrap gap-3" style={{ borderRadius: "0 8px 0 0" }}>
          <h5 className="m-0 font-weight-bold text-dark text-uppercase" style={{ fontSize: "1rem" }}>
            {activeTab === "General" ? "General Documents List" : "Personal Documents List"}
          </h5>
          
          <div className="d-flex gap-2 align-items-center flex-wrap">
            {/* Bộ lọc tìm kiếm nhanh */}
            <div className="position-relative">
               <input
                type="text"
                className="form-control form-control-sm ps-4"
                style={{ width: "220px", borderRadius: "20px" }}
                placeholder={`Search ${activeTab.toLowerCase()}s...`}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <i className="fas fa-search position-absolute text-muted" style={{ left: "12px", top: "8px", fontSize: "12px" }}></i>
            </div>

            {/* Nút thêm mới bản ghi */}
            <button
              className="btn btn-sm btn-primary px-3 rounded-pill shadow-sm fw-bold"
              onClick={() => {
                setEditingRegulation(null);
                setShowModal(true);
              }}
            >
              <i className="fas fa-plus me-1"></i> Add {activeTab}
            </button>
          </div>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th className="text-center" style={{ ...thStyle, width: "110px" }}>No. / Ver</th>
                  <th style={thStyle}>Classification</th>
                  <th style={thStyle}>Title & Content</th>
                  <th style={thStyle}>Effective Date</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Attachment</th>
                  <th className="text-center" style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentData.length > 0 ? (
                  currentData.map((r) => {
                    // --- TRƯỜNG HỢP LEVEL 1: DÒNG CÔNG TY ---
                    if (r.isCompanyRow) {
                      return (
                        <tr key={r.id} className="table-primary fw-bold" style={{ borderLeft: "5px solid #4e73df", backgroundColor: "#eaecf4" }}>
                          <td className="text-center">
                            <button
                              className="btn btn-xs btn-link p-0 text-decoration-none fw-bold text-primary"
                              onClick={() => toggleCompanyExpand(r.title)}
                              title={r.isExpanded ? "Collapse Company" : "Expand Company"}
                            >
                              <i className={`fas ${r.isExpanded ? "fa-minus-circle" : "fa-plus-circle"}`} style={{ fontSize: "1rem" }}></i>
                            </button>
                          </td>
                          <td colSpan="5">
                            <span className="text-uppercase text-primary tracking-wider" style={{ fontSize: "0.85rem", letterSpacing: "0.5px" }}>
                              <i className="fas fa-building me-2"></i> {r.title}
                            </span>
                          </td>
                          <td className="text-center">
                            <span className="badge bg-primary text-white text-uppercase" style={{ fontSize: "0.7rem" }}>Company Group</span>
                          </td>
                        </tr>
                      );
                    }

                    // --- TRƯỜNG HỢP LEVEL 2 & 3: DÒNG VĂN BẢN ---
                    return (
                      <tr 
                        key={r.id} 
                        className={r.isParentRow ? "bg-light-subtle table-light" : ""}
                        style={r.isParentRow ? { borderLeft: "4px solid #1cc88a" } : {}}
                      >
                        {/* Cột No. / Ver: Đóng/Mở các dòng con ở đây đối với Node Cha */}
                        <td className="text-center text-muted small fw-bold">
                          {r.isParentRow ? (
                            <div className="d-flex align-items-center justify-content-center gap-1">
                              {r.hasChildren ? (
                                <button
                                  className={`btn btn-xs btn-link p-0 text-decoration-none fw-bold ${r.isExpanded ? "text-danger" : "text-primary"}`}
                                  style={{ fontSize: "0.85rem", width: "18px", lineHeight: "1" }}
                                  onClick={() => toggleParentExpand(r.id)}
                                  title={r.isExpanded ? "Collapse revisions" : "Expand revisions"}
                                >
                                  <i className={`fas ${r.isExpanded ? "fa-minus-square" : "fa-plus-square"}`}></i>
                                </button>
                              ) : (
                                <span style={{ width: "18px" }}></span>
                              )}
                              <i className="fas fa-folder text-success opacity-70" style={{ fontSize: "0.95rem" }} title="Main Document"></i>
                            </div>
                          ) : (
                            <div className="d-flex align-items-center justify-content-center ps-3">
                              <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle px-2 py-1" style={{ fontSize: "0.75rem" }}>
                                {r.version ? `v${r.version}` : "v1.0"}
                              </span>
                            </div>
                          )}
                        </td>

                        {/* Phân loại tài liệu */}
                        <td>
                          <span className={`badge border px-2 py-1 text-dark fw-bold ${r.type === "General" ? "bg-light border-purple" : "bg-light border-info"}`}>
                            {r.type}
                          </span>
                        </td>

                        {/* Tiêu đề & Nội dung chi tiết - Thụt lề thụ động theo Level cấu trúc cây */}
                        <td>
                          <div className={r.isParentRow ? "fw-bold text-dark text-uppercase font-weight-black ps-3" : "text-dark ps-5"}>
                            {!r.isParentRow && <i className="fas fa-code-branch fa-rotate-90 me-2 text-muted opacity-50" style={{ fontSize: "0.8rem" }}></i>}
                            {r.title}
                          </div>
                          <small className={`text-muted text-truncate d-block ${r.isParentRow ? "ps-3" : "ps-5"}`} style={{ maxWidth: "350px" }}>
                            {r.content}
                          </small>
                        </td>

                        {/* Ngày hiệu lực */}
                        <td className="small">
                          {r.effectiveDate ? new Date(r.effectiveDate).toLocaleDateString("en-GB") : "---"}
                        </td>

                        {/* Trạng thái hiển thị */}
                        <td>
                          <span className={`badge px-2 py-1 ${r.status === "Public" ? "bg-success" : "bg-warning text-dark"}`}>
                            {r.status || "Active"}
                          </span>
                        </td>

                        {/* Tải tệp đính kèm */}
                        <td>
                          {r.documentUrl ? (
                            <a 
                              href={formatFileUrl(r.documentUrl)} 
                              download={`Document_${r.id || new Date().getTime()}`}
                              className="btn btn-xs btn-link text-success p-0 text-start small fw-bold"
                              style={{ textDecoration: "none", fontSize: "0.75rem" }}
                            >
                              <i className="fas fa-download me-1"></i> Download File
                            </a>
                          ) : (
                            <span className="text-muted small">---</span>
                          )}
                        </td>

                        {/* Cột các nút thao tác chức năng */}
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            {/* Nút Send Email CHỈ hiển thị ở dòng văn bản Con, dòng Cha hoặc dòng Công ty ẩn đi */}
                            {!r.isParentRow ? (
                              <button
                                className="btn btn-sm btn-outline-info text-info border-info-subtle rounded-circle shadow-sm"
                                style={{ width: "32px", height: "32px", padding: "0" }}
                                onClick={() => handleSendEmail(r.id)}
                                title="Send Notification Email"
                              >
                                <i className="fas fa-envelope"></i>
                              </button>
                            ) : (
                              <div style={{ width: "32px" }}></div>
                            )}

                            {/* Nút sửa bản ghi */}
                            <button
                              className="btn btn-sm btn-outline-primary border-0 rounded-circle shadow-sm"
                              style={{ width: "32px", height: "32px", padding: "0" }}
                              onClick={() => {
                                setEditingRegulation(r);
                                setShowModal(true);
                              }}
                              title="Edit"
                            >
                              <i className="fas fa-pencil-alt"></i>
                            </button>

                            {/* Nút xóa bản ghi */}
                            <button
                              className="btn btn-sm btn-outline-danger border-0 rounded-circle shadow-sm"
                              style={{ width: "32px", height: "32px", padding: "0" }}
                              onClick={() => handleDelete(r.id)}
                              title="Delete"
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-5 text-muted">
                      <i className="fas fa-folder-open d-block mb-2 fa-2x"></i>
                      No {activeTab.toLowerCase()} records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Phân trang danh sách */}
          <div className="d-flex justify-content-between align-items-center p-3 bg-light border-top">
            <span className="small text-muted fw-bold">
              Showing page {currentPage} of {totalPages || 1}
            </span>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button className="page-link border-0 shadow-sm mx-1 rounded-circle" onClick={() => setCurrentPage(currentPage - 1)}>
                    <i className="fas fa-chevron-left"></i>
                  </button>
                </li>
                {[...Array(totalPages)].map((_, i) => (
                  <li key={i} className={`page-item ${currentPage === i + 1 ? "active" : ""}`}>
                    <button 
                      className={`page-link border-0 shadow-sm mx-1 rounded-circle ${currentPage === i + 1 ? "bg-primary text-white" : "bg-white text-dark"}`} 
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                  <button className="page-link border-0 shadow-sm mx-1 rounded-circle" onClick={() => setCurrentPage(currentPage + 1)}>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>

      {/* Modal form thêm mới và cập nhật dữ liệu */}
      {showModal && (
        <RegulationModal
          show={showModal}
          initialData={editingRegulation}
          defaultType={activeTab}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default RegulationList;